/**
 * Labstep
 *
 * @module state/new/user-settings
 * @desc Selectors/Reducers for User Settings
 */

import { FolderType } from 'labstep-web/models/folder.model';
import type { LabstepReduxState } from 'labstep-web/state/types';
import type { Action } from 'redux';
import { createSelector } from 'reselect';

export interface StateUserSettings {
  folder_include_subfolders: Record<string, boolean>;
}

const initialState: StateUserSettings = {
  folder_include_subfolders: {
    [FolderType.experiment_workflow]: true,
    [FolderType.protocol_collection]: true,
  },
};

type ISetFolderIncludeSubfoldersActionType = {
  type: 'USER_SETTINGS_SET_FOLDER_INCLUDE_SUBFOLDERS';
  folderType: string;
  folder_include_subfolders: boolean;
};

export type ISetFolderIncludeSubfoldersActionCreatorType = (
  folderType: string,
  folder_include_subfolders: boolean,
) => ISetFolderIncludeSubfoldersActionType;

export const setFolderIncludeSubfoldersActionCreator: ISetFolderIncludeSubfoldersActionCreatorType =
  (folderType, folder_include_subfolders) => ({
    type: 'USER_SETTINGS_SET_FOLDER_INCLUDE_SUBFOLDERS',
    folderType,
    folder_include_subfolders,
  });

export const userSettings = (
  state = initialState,
  action: Action | ISetFolderIncludeSubfoldersActionType,
): StateUserSettings => {
  if (action.type === 'USER_SETTINGS_SET_FOLDER_INCLUDE_SUBFOLDERS') {
    const typedAction =
      action as ISetFolderIncludeSubfoldersActionType;
    return {
      ...state,
      folder_include_subfolders: {
        ...state.folder_include_subfolders,
        [typedAction.folderType]:
          typedAction.folder_include_subfolders,
      },
    };
  }
  return state;
};

export const userSettingsReducer = userSettings;

// Selectors
export const selectUserSettings = createSelector(
  (state: LabstepReduxState) => state.user_settings,
  (userSettings: StateUserSettings | undefined) =>
    userSettings || initialState,
);

export const selectFolderIncludeSubfolders = (folderType: string) =>
  createSelector(
    selectUserSettings,
    (userSettings: StateUserSettings) =>
      userSettings.folder_include_subfolders[folderType] ?? true,
  );
